// ======================
// SupportWidget
// ======================

import { observer } from "mobx-react-lite";
import { Drawer, Fab, SvgIcon, Theme, Tooltip } from "@mui/material";
import * as React from "react";
import { useRightSideDrawerWidth } from "../layout/MainLayout";
import { useAppServices } from "../app/services";
import { getCustomerCenterAuthCallbackUrl } from "./HelpCommon";
import { CloseIcon } from "../../common/CommonIcons";
import { MdHeadsetMic } from "react-icons/md";
import { useHelpDrawerState } from "./hooks/help_hooks";
import { shallow } from "zustand/shallow";
import { BiLinkExternal } from "react-icons/bi";
import { HideInPrivateEdition } from "../auth/PrivateEditionView";
import { useTranslation } from "react-i18next";

interface HelpDrawerProps {}

const URL_KEYSTRING = "customer-center-url-changed:";

export const HelpDrawer: React.FC<HelpDrawerProps> = observer((p) => {
    const { t } = useTranslation();
    const { setHelpDrawerOpen, helpDrawerOpen, helpCenterUrl, setHelpCenterUrl } = useHelpDrawerState(
        (s) => ({
            setHelpDrawerOpen: s.setHelpDrawerOpen,
            helpDrawerOpen: s.helpDrawerOpen,
            helpCenterUrl: s.helpCenterUrl,
            setHelpCenterUrl: s.setHelpCenterUrl,
        }),
        shallow
    );
    const helpDrawerWidth = useRightSideDrawerWidth();

    // setup useEffect to listen to messages from the iframe
    React.useEffect(() => {
        const listener = (event: MessageEvent) => {
            if (typeof event.data === "string") {
                if (event.data.includes(URL_KEYSTRING)) {
                    setHelpCenterUrl(event.data.replace(URL_KEYSTRING, "").replace("?from=cmc", ""));
                }
            }
        };
        window.addEventListener("message", listener);
        return () => window.removeEventListener("message", listener);
    }, [setHelpCenterUrl]);

    return (
        <>
            <Drawer
                variant={"persistent"}
                open={helpDrawerOpen}
                onClose={() => setHelpDrawerOpen(false)}
                sx={{
                    width: helpDrawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: helpDrawerWidth,
                        zIndex: (t: Theme) => t.zIndex.modal + 1,
                        background: (t: Theme) => t.palette.cirrus.medium,
                        borderLeftWidth: 2,
                        borderLeftColor: (t: Theme) => t.palette.cirrus.light,
                        borderLeftStyle: "solid",
                        marginTop: 0,
                        boxSizing: "border-box",
                    },
                }}
                elevation={0}
                anchor={"right"}
            >
                <iframe title={t("common:links.customerCenterFrame")} src={helpCenterUrl} height={"100%"} width={"100%"} style={{ border: "none" }}></iframe>
            </Drawer>
        </>
    );
});

// ======================
// HelpActionButton
// ======================

export const HelpActionButton: React.FC<HelpDrawerProps> = observer((p) => {
    const { t } = useTranslation();
    const { setHelpCenterUrl, setHelpDrawerOpen, helpDrawerOpen, helpCenterUrl } = useHelpDrawerState(
        (s) => ({
            setHelpCenterUrl: s.setHelpCenterUrl,
            setHelpDrawerOpen: s.setHelpDrawerOpen,
            helpDrawerOpen: s.helpDrawerOpen,
            helpCenterUrl: s.helpCenterUrl,
        }),
        shallow
    );

    const handleClick = () => {
        if (!helpDrawerOpen) {
            setHelpCenterUrl(getCustomerCenterAuthCallbackUrl("kb?from=cmc"));
            setHelpDrawerOpen(true);
        } else {
            setHelpDrawerOpen(false);
        }
    };

    return (
        <HideInPrivateEdition>
            <>
                {helpDrawerOpen && (
                    <Tooltip title={t("common:links.openInNewTab")}>
                        <Fab
                            color={"secondary"}
                            size={"medium"}
                            sx={{ zIndex: 5000, position: "fixed", bottom: 25, right: 80 }}
                            onClick={() => window.open(helpCenterUrl, "_blank")}
                        >
                            <BiLinkExternal size={20} />
                        </Fab>
                    </Tooltip>
                )}
                <Tooltip title={helpDrawerOpen ? t("common:actions.close") : t("layout:help")}>
                    <Fab
                        color={helpDrawerOpen ? "secondary" : "primary"}
                        size={"medium"}
                        sx={{ zIndex: 5000, position: "fixed", bottom: 25, right: 25 }}
                        onClick={handleClick}
                    >
                        {helpDrawerOpen ? (
                            <CloseIcon />
                        ) : (
                            <SvgIcon>
                                <MdHeadsetMic />
                            </SvgIcon>
                        )}
                    </Fab>
                </Tooltip>
            </>
        </HideInPrivateEdition>
    );
});
